// Libraries
import {
  Directive,
  Input,
  OnInit,
  HostListener,
  Inject,
  ElementRef
} from '@angular/core';
import * as lodash from 'lodash';
import * as CountUp from 'countup.js';

@Directive({
  selector: '[wpCounterUp]'
})
export class CounterUpDirective implements OnInit {
  // Public properties
  /**
   * Optional extra configuration, such as easing.
   */
  @Input() public options: any;

  /**
   * Optional start value for the count. Defaults to zero.
   */
  @Input() public startVal: number;

  /**
   * Optional duration of the animation. Default is two seconds.
   */
  @Input() public duration: number;

  /**
   * Optional number of decimal places. Default is two.
   */
  @Input() public decimals: number;

  /**
   * Optional flag for specifying whether the element should re-animate when clicked.
   * Default is true.
   */
  @Input() public reanimateOnClick: boolean;

  /**
   * The value to count up to.
   */
  private endValue: number;
  @Input()
  get endVal(): number {
      return this.endValue;
  }

  set endVal(value: number) {
      this.endValue = value;

      if (isNaN(value)) {
          return;
      }

      if (!this.countUp) {
          return;
      }

      this.countUp.update(value);
  }

  // Private properties
  /**
   * Count up element.
   */
  private countUp: any;
  /**
   * Default values
   */
  private defaultValues = {
    decimals: 0,
    duration: 2,
    end: 0,
    start: 0
  };

  constructor(@Inject(ElementRef) private el: ElementRef) {}

  public ngOnInit() {
    this.countUp = this.createCountUp(this.startVal, this.endVal, this.decimals, this.duration);
    this.animate();
  }

  /**
   * Re-animate if preference is set.
   */
  @HostListener('click')
  public onClick() {
    if (this.reanimateOnClick) {
        this.animate();
    }
  }

  /**
   * Create counter up.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @param start Start value
   * @param end End value
   * @param decimals Decimals count
   * @param duration Counter duration
   * @returns
   * @memberof CounterUpDirective
   */
  private createCountUp(start: number, end: number, duration: number, decimals: number) {
    start = lodash.toNumber(start) || this.defaultValues.start;
    end = lodash.toNumber(end) || this.defaultValues.end;
    duration = lodash.toNumber(duration) || this.defaultValues.duration;
    decimals = lodash.toNumber(decimals) || this.defaultValues.decimals;

    // Construct countUp
    let countUp = new CountUp(this.el.nativeElement, start, end, decimals, duration, this.options);
    if (end > 999) {
      // Make easing smoother for large numbers
      countUp = new CountUp(this.el.nativeElement, start, end - 100, decimals, duration / 2, this.options);
    }

    return countUp;
  }

  /**
   * Animate counter up.
   *
   * @author Joaquin Volpe <joaquin.volpe@woopasoft.com>
   * @memberof CounterUpDirective
   */
  private animate() {
    this.countUp.reset();
    if (this.endVal > 999) {
      this.countUp.start(() => this.countUp.update(this.endVal));
    } else {
      this.countUp.start();
    }
  }
}
